import { alpha, createTheme, darken } from '@mui/material'
import * as locales from '@mui/material/locale'
import _ from 'lodash'
import tailwind from '~root/tailwind.config.js'

// Tailwind v4 removed resolveConfig; read theme from raw config (theme + theme.extend)
const themeExtend = tailwind.theme?.extend ?? {}
const themeBase = tailwind.theme ?? {}
const themeColors = { ...themeBase.colors, ...themeExtend.colors }
const themeFontFamily = { ...themeBase.fontFamily, ...themeExtend.fontFamily }
const themeFontSize = { ...themeBase.fontSize, ...themeExtend.fontSize }
const themeFontWeight = { ...themeBase.fontWeight, ...themeExtend.fontWeight }

const tailwindTheme = {
  colors: themeColors,
  fontFamily: themeFontFamily,
  fontSize: themeFontSize,
  fontWeight: themeFontWeight,
}

const brandColors = {
  bosch: themeColors.bosch?.DEFAULT,
  siemens: themeColors.siemens?.DEFAULT,
  zelmer: themeColors.zelmer?.DEFAULT,
}

const common = {
  // typography: tailwindTheme.fontWeight,
  typography: {
    fontFamily: tailwindTheme.fontFamily.sans,
    h2: {
      fontSize: '1.125rem',
      fontWeight: 700,
    },
    h3: {
      fontSize: '1rem',
      fontWeight: 700,
    },
    small: {
      fontSize: '0.8em',
    },
  },
}

// https://mui.com/material-ui/customization/default-theme/

const createBrandTheme = (mode, brand) => {
  const theme = createTheme(
    _.merge(common, {
      components: {
        MuiButton: {
          styleOverrides: {
            root: ({ ownerState }) => ({
              ...(ownerState.variant === 'outlined' &&
                ownerState.color === 'primary' &&
                (mode === 'light'
                  ? {
                      borderColor: 'rgba(0, 0, 0, 0.35)',
                      color: 'rgba(0, 0, 0, 0.75)',
                    }
                  : {
                      borderColor: 'rgba(255, 255, 255, 0.35)',
                      color: 'rgba(255, 255, 255, 0.75)',
                    })),
              '&:hover': {
                backgroundColor:
                  mode === 'light'
                    ? darken(brandColors[brand], 0.3)
                    : darken(brandColors[brand], 0.48),
              },
            }),
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderColor:
                mode === 'light'
                  ? brandColors[brand]
                  : darken(brandColors[brand], 0.25),
              borderStyle: 'dotted',
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: ({ ownerState }) => ({
              ...(ownerState.variant === 'background' && {
                color: mode === 'light' ? '#ffffff' : 'text.primary',
                '&:hover': {
                  backgroundColor:
                    mode === 'light'
                      ? darken(brandColors[brand], 0.3)
                      : darken(brandColors[brand], 0.48),
                },
              }),
              '&.Mui-disabled': {
                ...(ownerState.variant === 'background' && {
                  backgroundColor:
                    mode === 'light'
                      ? darken(brandColors[brand], 0.3)
                      : darken(brandColors[brand], 0.48),
                  color: 'rgba(255, 255, 255, .5)',
                }),
              },
            }),
          },
        },
        MuiStack: {
          defaultProps: {
            useFlexGap: true,
          },
        },
        MuiTooltip: {
          defaultProps: {
            arrow: true,
          },
          styleOverrides: {
            popper: {
              pointerEvents: 'none',
              userSelect: 'none',
            },
          },
        },
      },
      palette: {
        mode,
        primary: {
          default: brandColors[brand],
          main:
            mode === 'light'
              ? brandColors[brand]
              : darken(brandColors[brand], 0.25),
          dark:
            mode === 'light'
              ? darken(brandColors[brand], 0.3)
              : darken(brandColors[brand], 0.48),
        },
        action: {
          hover: alpha(mode === 'light' ? '#000000' : '#ffffff', 0.05),
          // hoverOpacity: 0.1,
          selected: alpha(brandColors[brand], 0.08),
          selectedOpacity: 0.75,
          focus: alpha(brandColors[brand], 0.12),
          focusOpacity: 0.12,
        },
        background: {
          default: mode === 'light' ? '#ffffff' : '#121212',
          button:
            mode === 'light' ? 'rgba(0, 0, 0, .1)' : 'rgba(255, 255, 255, .1)',
          paper: mode === 'light' ? '#f0f0f0' : '#121212',
        },
        danger: tailwindTheme.colors.danger.DEFAULT,
        inactive: {
          background: mode === 'light' ? '#999999' : '#555555',
          text: '#888888',
        },
        input: {
          border: {
            default:
              mode === 'light'
                ? 'rgba(0, 0, 0, 0.23)'
                : 'rgba(255, 255, 255, 0.23)',
            focused:
              mode === 'light'
                ? brandColors[brand]
                : darken(brandColors[brand], 0.25),
            hover: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : '#ffffff',
            highlighted: 'orange',
          },
        },
      },
      typography: {
        h1: {
          color: brandColors[brand],
          fontSize: tailwindTheme.fontSize.xl,
          fontWeight: tailwindTheme.fontWeight.bold,
        },
        h2: {
          color: brandColors[brand],
        },
        h3: {
          color: brandColors[brand],
        },
        annotation: {
          fontSize: '0.8rem',
          opacity: 0.5,
        },
        header: {
          fontWeight: tailwindTheme.fontWeight.bold,
        },
        picto: {
          fontWeight: tailwindTheme.fontWeight.bold,
        },
      },
    }),
    locales.plPL
  )
  theme.breakpoints.values.md = 1000
  theme.palette.text.disabled = theme.palette.text.primary
  return theme
}

const createThemeByBrand = (mode, brand) => {
  return createBrandTheme(mode, brand ?? 'bosch')
  /* switch (brand) {
    case 'bosch':
    case 'siemens':
    case 'zelmer':
      return createBrandTheme(mode, brand)
    default:
      return createTheme(common, locales.plPL)
  } */
}

export default createThemeByBrand
